import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { Theme } from '@mui/material'

import {
  getCurrentUser,
  getFacebookConnectData,
  getInstagramConnectData,
  initiateFacebookConnect,
  logout,
  performConnect,
  performDisconnect,
  initiateInstagramConnect,
  setNavigationItem,
  setNavigationList,
  setLicensePermissions,
  setTheme,
  setUser,
  setFacebookConnectHash,
  setInstagramConnectHash,
  setConnectedFacebookPage,
  setConnectedInstagramPage,
  getSmPublishAuthToken,
  setGoogleConnectHash,
  setConnectedGooglePage,
  initiateGoogleConnect,
  socialmediaUserConnect,
  socialmediaUserDisconnect,
  socialmediaEditorConnect,
  socialmediaEditorDisconnect,
  setMessage,
  setDevelopmentSystem,
  updateUserAccount,
  requestStarted,
  requestFinished,
  requestFailed,
  userLogin,
  setLanguage,
  setInstagramNumberOfPost,
  getPageIdentifiersForUser,
  getPotentialPageImprint,
  setPageIdentifiersForUser,
  setTokenChanged,
  setConnectedLinkedInPage,
  getUserByPnr,
  resetPnrUser,
  acceptAnnouncement,
  updateAnnouncement,
  getActiveAnnouncement,
  getAnnouncementTemplate,
} from './common.actions'
import { ActionCall, Languages, Navigation, SocialNetworkLicensePermissions } from '@professocial/professocialTypes/types/commonTypes'

import getAppTheme from '../../defaults/getAppTheme'
import { produce } from 'immer'
import { ApiError } from '../coreRoot.reducer'
import { FacebookConnectDataElement, InstagramConnectDataElement } from '@3m5/sm_publish-frontend-api'
import { Message } from '@professocial/professocialTypes/types/messageTypes'
import { EventType, Severity } from '@professocial/professocialTypes/types'
import i18n from 'i18next'
import { PageIdentifier, PnrUser, UserSettings } from '@professocialApi/professocial-core-frontend-api'
import { Imprint } from '@professocialApi/professocial-core-frontend-api/models/Imprint'
import { AnnouncementResponse } from '@professocialApi/professocial-core-frontend-api/models/AnnouncementResponse'

export interface CommonState {
  fetching: boolean;
  error: ApiError;
  theme: Theme;
  user?: UserSettings;
  message?: Message;
  connectionChanged: boolean;
  tokenChanged: boolean;
  facebookConnectHash?: string;
  instagramConnectHash?: string;
  googleConnectHash?: string;
  facebookRedirect?: string;
  instagramRedirect?: string;
  googleRedirect?: string;
  facebookPages?: FacebookConnectDataElement[];
  instagramPages?: InstagramConnectDataElement[];
  connectedFacebookPage: string;
  developmentSystem: boolean;
  connectedInstagramPage: string;
  connectedGooglePage: string;
  connectedLinkedInPage: string;
  licensePermissions: SocialNetworkLicensePermissions;
  navigationList: Navigation[];
  navigationItem?: string;
  languages?: Languages[];
  requests: ActionCall[];
  instagramNumberOfPosts: 24 | 9;
  userPages?: PageIdentifier[];
  imprint?: Imprint;
  pnrUser?: PnrUser;
  activeAnnouncement?: AnnouncementResponse;
  announcementTemplate?: AnnouncementResponse;
}

const INITIAL_STATE: CommonState = {
  theme: getAppTheme(),
  connectionChanged: false,
  tokenChanged: false,
  developmentSystem: false,
  licensePermissions: {
    facebook: true,
    instagram: true,
    googleMyBusiness: false,
  },
  connectedFacebookPage: window.localStorage.getItem('facebookPageNetworkId') || '',
  connectedInstagramPage: window.localStorage.getItem('instagramPageNetworkId') || '',
  connectedGooglePage: window.localStorage.getItem('googlePageNetworkId') || '',
  connectedLinkedInPage: window.localStorage.getItem('linkedInPageNetworkId') || '',
  navigationItem: 'dashboard',
  navigationList: [],
  fetching: false,
  error: {},
  requests: [],
  instagramNumberOfPosts: 24,
  pnrUser: undefined,
  activeAnnouncement: undefined,
}

const triggerProfessocialLogout = () => {
  window.dispatchEvent(new CustomEvent('ProfessocialLogout'))
}

export const triggerProfessocialMessage = (data: Message) => {
  window.dispatchEvent(new CustomEvent('ProfessocialMessage', { detail: data }))
}

export const CommonReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setFacebookConnectHash, (state, facebookConnectHash) => {
    return ({ ...state, facebookConnectHash })
  })
  .case(setInstagramNumberOfPost, (state, instagramNumberOfPosts) => {
    return ({ ...state, instagramNumberOfPosts })
  })
  .case(setInstagramConnectHash, (state, instagramConnectHash) => {
    return ({ ...state, instagramConnectHash })
  })
  .case(setGoogleConnectHash, (state, googleConnectHash) => {
    return ({ ...state, googleConnectHash })
  })
  .case(setPageIdentifiersForUser, (state, userPages) => {
    return ({ ...state, userPages })
  })
  .case(setTokenChanged, (state, tokenChanged) => {
    return ({ ...state, tokenChanged })
  })
  .case(setConnectedFacebookPage, (state, connectedFacebookPage) => {
    window.localStorage.setItem('facebookPageNetworkId', connectedFacebookPage)
    return ({ ...state, connectedFacebookPage })
  })
  .case(setConnectedInstagramPage, (state, connectedInstagramPage) => {
    window.localStorage.setItem('instagramPageNetworkId', connectedInstagramPage)
    return ({ ...state, connectedInstagramPage })
  })
  .case(setConnectedGooglePage, (state, connectedGooglePage) => {
    window.localStorage.setItem('googlePageNetworkId', connectedGooglePage)
    return ({ ...state, connectedGooglePage })
  })
  .case(setConnectedLinkedInPage, (state, connectedLinkedInPage) => {
    window.localStorage.setItem('linkedInPageNetworkId', connectedLinkedInPage)
    return ({ ...state, connectedLinkedInPage })
  })
  .case(setTheme, (state, theme) => {
    return ({ ...state, theme })
  })
  .case(setUser, (state, user) => {
    return ({ ...state, user })
  })
  .case(setLicensePermissions, (state, licensePermissions) => {
    return ({ ...state, licensePermissions })
  })
  .case(setNavigationList, (state, navigationList) => {
    return ({ ...state, navigationList })
  })
  .case(setNavigationItem, (state, navigationItem) => {
    return ({ ...state, navigationItem })
  })
  .case(setDevelopmentSystem, (state, developmentSystem) => {
    return ({ ...state, developmentSystem })
  })
  .case(setLanguage, (state, languages) => {
    return ({ ...state, languages })
  })

  /**
   *  Requests Management
   */
  .case(requestStarted, (state, action) => {
    const clearStateRequests = state.requests.filter(request => request.status !== 'success')

    clearStateRequests.push({ type: action.type, status: 'pending', inProgress: true, error: null, id: action.id })
    return { ...state, requests: clearStateRequests }
  })
  .case(requestFinished, (state, action) => {
    return produce(state, (draft) => {
      const index = draft.requests.findIndex(request => request.type === action.type && request.id === action.id)
      if (index !== -1) {
        draft.requests[index].inProgress = false
        draft.requests[index].status = 'success'
        draft.requests[index].error = null
      }
      setTimeout(() => action.message && triggerProfessocialMessage({
        severity: action.message.severity,
        message: action.message.message,
        eventType: action.message.eventType,
      }))
    })
  })
  .case(requestFailed, (state, action) => {
    return produce(state, (draft) => {
      const index = draft.requests.findIndex(request => request.type === action.type)
      if (index !== -1) {
        draft.requests[index].inProgress = false
        draft.requests[index].status = 'failed'
        draft.requests[index].error = action.error
      }

      setTimeout(() => action.message && triggerProfessocialMessage({
        severity: action.message.severity,
        message: action.message.message,
        eventType: action.message.eventType,
      }))
    })
  })

  /**
   * Message Management
   */
  .case(setMessage, (state, message) => {
    // Fix - wait while the reducer is executing
    setTimeout(() => triggerProfessocialMessage(message))

    return produce(state, (draft) => {
      draft.message = message
    })
  })

  /**
   * userLogin
   */
  .case(userLogin.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.userLogin
    })
  })
  .case(userLogin.done, (state, { result }) => {
    return produce(state, draft => {
      result.id && window.localStorage.setItem('user', result.id)
      draft.user = result
      draft.fetching = false
    })
  })
  .case(userLogin.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.user = undefined
      }
      draft.error.userLogin = errorStatus
    })
  })

  /**
   * logout
   */
  .case(logout.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.logout
    })
  })

  .case(logout.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.user = undefined
      triggerProfessocialLogout()
      return draft
    })
  })

  .case(logout.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.logout = (error as Response)
    })
  })

  /**
   * getUserAccount
   */
  .case(getCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getCurrentUser
    })
  })

  .case(getCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.user = result
      draft.fetching = false
      return draft
    })
  })

  .case(getCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getCurrentUser = (error as Response)
    })
  })

  /**
   * initiateFacebookConnect
   */
  .case(initiateFacebookConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.initiateFacebookConnect
    })
  })

  .case(initiateFacebookConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.facebookConnectHash = result.connectHash
      draft.fetching = false
      return draft
    })
  })

  .case(initiateFacebookConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.initiateFacebookConnect = (error as Response)
    })
  })

  /**
   * initiateInstagramConnect
   */
  .case(initiateInstagramConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.initiateInstagramConnect
    })
  })

  .case(initiateInstagramConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.instagramConnectHash = result.connectHash
      draft.fetching = false
      return draft
    })
  })

  .case(initiateInstagramConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.initiateInstagramConnect = (error as Response)
    })
  })

  /**
   * initiateGoogleConnect
   */
  .case(initiateGoogleConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.initiateGoogleConnect
    })
  })

  .case(initiateGoogleConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.googleConnectHash = result.connectHash
      draft.fetching = false
      return draft
    })
  })

  .case(initiateGoogleConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.initiateGoogleConnect = (error as Response)
    })
  })

  /**
   * getFacebookConnectData
   */
  .case(getFacebookConnectData.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getFacebookConnectData
    })
  })

  .case(getFacebookConnectData.done, (state, { result }) => {
    return produce(state, draft => {
      draft.facebookPages = result
      draft.connectionChanged = false
      draft.fetching = false
      return draft
    })
  })

  .case(getFacebookConnectData.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getFacebookConnectData = (error as Response)
    })
  })

  /**
   * getInstagramConnectData
   */
  .case(getInstagramConnectData.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getInstagramConnectData
    })
  })

  .case(getInstagramConnectData.done, (state, { result }) => {
    return produce(state, draft => {
      draft.instagramPages = result
      draft.connectionChanged = false
      draft.fetching = false
      return draft
    })
  })

  .case(getInstagramConnectData.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getInstagramConnectData = (error as Response)
    })
  })

  /**
   * performConnect
   */
  .case(performConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.initiateConnect
    })
  })

  .case(performConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.connectionChanged = true
      return draft
    })
  })

  .case(performConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.performConnect = (error as Response)
    })
  })

  /**
   * performDisconnect
   */
  .case(performDisconnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.performDisconnect
    })
  })

  .case(performDisconnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.connectionChanged = true
      return draft
    })
  })

  .case(performDisconnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.performDisconnect = (error as Response)
    })
  })

  /**
   * getSmPublishAuthToken
   */
  .case(getSmPublishAuthToken.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getSmPublishAuthToken
    })
  })

  .case(getSmPublishAuthToken.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.tokenChanged = true
      sessionStorage.setItem('X-F2M-AuthToken', result.jwt)
      return draft
    })
  })

  .case(getSmPublishAuthToken.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getSmPublishAuthToken = (error as Response)
    })
  })

  /**
   * socialmediaUserConnect
   */
  .case(socialmediaUserConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.socialmediaUserConnect
    })
  })

  .case(socialmediaUserConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.facebookRedirect = result.authorizationUrl
      return draft
    })
  })

  .case(socialmediaUserConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.socialmediaUserConnect = (error as Response)
    })
  })

  /**
   * socialmediaUserDisconnect
   */
  .case(socialmediaUserDisconnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.socialmediaUserDisconnect
    })
  })

  .case(socialmediaUserDisconnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.connectionChanged = true
      return draft
    })
  })

  .case(socialmediaUserDisconnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.socialmediaUserDisconnect = (error as Response)
    })
  })
  /**
   * socialmediaEditorConnect
   */
  .case(socialmediaEditorConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.socialmediaEditorConnect
    })
  })

  .case(socialmediaEditorConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.facebookRedirect = result.authorizationUrl
      return draft
    })
  })

  .case(socialmediaEditorConnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.socialmediaEditorConnect = (error as Response)
    })
  })

  /**
   * socialmediaEditorDisconnect
   */
  .case(socialmediaEditorDisconnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.socialmediaEditorDisconnect
    })
  })

  .case(socialmediaEditorDisconnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.connectionChanged = true
      return draft
    })
  })

  .case(socialmediaEditorDisconnect.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.socialmediaEditorDisconnect = (error as Response)
    })
  })

  .case(getUserByPnr.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getUserByPnr
    })
  })
  .case(getUserByPnr.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.pnrUser = result
      return draft
    })
  })
  .case(getUserByPnr.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getUserByPnr = (error as Response)
    })
  })
  .case(resetPnrUser, (state) => {
    return produce(state, (draft) => {
      draft.pnrUser = undefined
    })
  })

  /**
   * updateUserAccount
   */
  .case(updateUserAccount.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateUserAccount
    })
  })

  .case(updateUserAccount.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      // draft.user = result
      triggerProfessocialMessage({
        severity: Severity.success,
        message: i18n.t('coreMessages:isSuccessfullyUpdated', { context: i18n.t('accountsCommon:user') }),
        eventType: EventType.update,
      })
      return draft
    })
  })
  .case(updateUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateUserAccount = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: i18n.t('coreMessages:updateError', { context: i18n.t('accountsCommon:user') }),
        eventType: EventType.update,
      })
    })
  })
  .case(getPageIdentifiersForUser.done, (state: CommonState, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.userPages = result
      return draft
    })
  })

  /**
   * getPotentialPageImprint
   */
  .case(getPotentialPageImprint.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.imprint
    })
  })
  .case(getPotentialPageImprint.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.imprint = result
    })
  })
  .case(getPotentialPageImprint.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.imprint = (error as Response)
    })
  })

// == Announcement == 

  .case(getActiveAnnouncement.done, (state, { result }) =>
    produce(state, draft => {
      draft.activeAnnouncement = result
    })
  )

  // When the current announcement is updated
  .case(updateAnnouncement.done, (state, { result }) =>
    produce(state, draft => {
      draft.activeAnnouncement = result
    })
  )

  // When an announcement is accepted
  .case(acceptAnnouncement.done, (state) =>
    produce(state, () => {

    })
  )

  .case(getAnnouncementTemplate.done, (state, { result }) =>
    produce(state, draft => {
      draft.announcementTemplate = result
    })
  )
